import { useState, useMemo } from 'react';

export default function DayByDayBuilder({ activities = [], restaurants = [], startDate, endDate, destinationCity }) {
  const numDays = useMemo(() => {
    if (startDate && endDate) {
      const s = new Date(startDate + 'T12:00:00');
      const e = new Date(endDate + 'T12:00:00');
      const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
      return diff > 0 ? diff : 7;
    }
    return 7;
  }, [startDate, endDate]);

  const [dayPlan, setDayPlan] = useState(() => {
    const plan = {};
    for (let i = 0; i < numDays; i++) plan[i] = [];
    return plan;
  });

  const [selectedDay, setSelectedDay] = useState(0);

  const assignedIds = useMemo(() => {
    const ids = new Set();
    Object.values(dayPlan).forEach(items => items.forEach(item => ids.add(item._uid)));
    return ids;
  }, [dayPlan]);

  const allItems = useMemo(() => {
    const acts = activities.map(a => ({ ...a, _type: 'activity', _uid: `act-${a.id || a.name}` }));
    const rests = restaurants.map(r => ({ ...r, _type: 'restaurant', _uid: `rest-${r.id || r.name}` }));
    return [...acts, ...rests];
  }, [activities, restaurants]);

  const available = allItems.filter(item => !assignedIds.has(item._uid));

  function addItem(item, dayIndex) {
    setDayPlan(prev => ({
      ...prev,
      [dayIndex]: [...(prev[dayIndex] || []), item],
    }));
  }

  function removeItem(dayIndex, uid) {
    setDayPlan(prev => ({
      ...prev,
      [dayIndex]: prev[dayIndex].filter(item => item._uid !== uid),
    }));
  }

  function getDayDate(dayIndex) {
    if (!startDate) return null;
    const d = new Date(startDate + 'T12:00:00');
    d.setDate(d.getDate() + dayIndex);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function getDayCost(items) {
    return items.reduce((sum, item) => {
      const cost = item._type === 'restaurant' ? (item.avg_meal_for_two || 0) : (item.price_for_two || 0);
      return sum + cost;
    }, 0);
  }

  const totalActivities = Object.values(dayPlan).flat().filter(i => i._type === 'activity').length;
  const totalRestaurants = Object.values(dayPlan).flat().filter(i => i._type === 'restaurant').length;
  const totalCost = Object.values(dayPlan).flat().reduce((sum, item) => {
    return sum + (item._type === 'restaurant' ? (item.avg_meal_for_two || 0) : (item.price_for_two || 0));
  }, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-white">Itinerary Builder</h3>
        {destinationCity && <span className="text-xs text-brand-ink/55">{destinationCity}</span>}
      </div>

      {/* Available items panel */}
      <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-brand-ink/65 uppercase tracking-wider">Available Items</span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-ink/55">Add to:</label>
            <select
              value={selectedDay}
              onChange={e => setSelectedDay(Number(e.target.value))}
              className="bg-brand-ink/10 text-white text-xs rounded-lg px-2 py-1 border border-brand-ink/10 outline-none"
            >
              {Array.from({ length: numDays }, (_, i) => (
                <option key={i} value={i} className="bg-navy text-white">Day {i + 1}</option>
              ))}
            </select>
          </div>
        </div>

        {available.length === 0 ? (
          <div className="text-xs text-brand-ink/45 italic">All items assigned — nice planning!</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {available.map(item => (
              <button
                key={item._uid}
                onClick={() => addItem(item, selectedDay)}
                className="flex items-center gap-1.5 bg-brand-ink/5 hover:bg-brand-ink/10 border border-brand-ink/10 rounded-lg px-3 py-1.5 text-xs transition group"
              >
                <div className={`w-1.5 h-1.5 rounded-full ${item._type === 'activity' ? 'bg-teal' : 'bg-gold'}`} />
                <span className="text-brand-ink/80">{item.name}</span>
                <span className="text-brand-ink/55 group-hover:text-teal transition">+</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Day cards */}
      <div className="space-y-3">
        {Array.from({ length: numDays }, (_, dayIndex) => {
          const items = dayPlan[dayIndex] || [];
          const dayCost = getDayCost(items);
          const dateStr = getDayDate(dayIndex);
          const isPacked = items.length > 4;

          const morningItems = items.slice(0, Math.ceil(items.length / 3));
          const afternoonItems = items.slice(Math.ceil(items.length / 3), Math.ceil((items.length * 2) / 3));
          const eveningItems = items.slice(Math.ceil((items.length * 2) / 3));

          return (
            <div key={dayIndex} className="bg-navy-light rounded-xl border border-brand-ink/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="font-semibold text-sm text-white">Day {dayIndex + 1}</span>
                  {dateStr && <span className="text-xs text-brand-ink/55 ml-2">— {dateStr}</span>}
                </div>
                <span className="text-xs text-brand-ink/55">{items.length} item{items.length !== 1 ? 's' : ''}</span>
              </div>

              {items.length === 0 ? (
                <div className="text-xs text-brand-ink/45 italic py-2">No items yet — add some from the panel above</div>
              ) : (
                <div className="space-y-3">
                  {/* Morning */}
                  {morningItems.length > 0 && (
                    <TimeSlot label="Morning" items={morningItems} dayIndex={dayIndex} onRemove={removeItem} />
                  )}
                  {/* Afternoon */}
                  {afternoonItems.length > 0 && (
                    <TimeSlot label="Afternoon" items={afternoonItems} dayIndex={dayIndex} onRemove={removeItem} />
                  )}
                  {/* Evening */}
                  {eveningItems.length > 0 && (
                    <TimeSlot label="Evening" items={eveningItems} dayIndex={dayIndex} onRemove={removeItem} />
                  )}
                </div>
              )}

              {isPacked && (
                <div className="text-xs text-amber-400 mt-2">
                  Packed day — consider moving something
                </div>
              )}

              {items.length > 0 && (
                <div className="flex justify-end mt-3 pt-2 border-t border-brand-ink/5">
                  <span className="text-xs text-gold font-semibold">${dayCost} for two</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary bar */}
      <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-teal" />
              <span className="text-xs text-brand-ink/70">{totalActivities} activities</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-gold" />
              <span className="text-xs text-brand-ink/70">{totalRestaurants} restaurants</span>
            </div>
          </div>
          <span className="text-sm font-bold text-teal">Total: ${totalCost} for two</span>
        </div>
      </div>
    </div>
  );
}

function TimeSlot({ label, items, dayIndex, onRemove }) {
  return (
    <div>
      <div className="text-[10px] font-bold text-brand-ink/55 uppercase tracking-wider mb-1">{label}</div>
      <div className="space-y-1">
        {items.map(item => (
          <div key={item._uid} className="flex items-center justify-between py-1.5 group">
            <div className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${item._type === 'activity' ? 'bg-teal' : 'bg-gold'}`} />
              <div>
                <span className="text-sm text-brand-ink/85">{item.name}</span>
                <span className="text-[11px] text-brand-ink/55 ml-2">
                  {item.duration || item.cuisine || ''}
                  {(item.price_for_two || item.avg_meal_for_two) ? ` · $${item.price_for_two || item.avg_meal_for_two}` : ''}
                </span>
              </div>
            </div>
            <button
              onClick={() => onRemove(dayIndex, item._uid)}
              className="text-brand-ink/45 hover:text-danger text-sm opacity-0 group-hover:opacity-100 transition px-1"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
